import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { getActiveModule } from '@/utils/activeModule';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import {
  ChevronRight,
  ChevronLeft,
  Ruler,
  Wrench,
  Database,
  Rocket,
  HelpCircle,
  CheckCircle,
  AlertTriangle,
  Calculator,
  Upload,
  BookOpen,
  Settings2,
  Printer,
  Flame,
  Router,
  Lock
} from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { IMAGES, bgStyle } from '@/config/images';

const TOTAL_STEPS = 4;

const MODULE_KEYS = {
  CNC:   'cnc_routing',
  PRINT: '3d_printing',
  LASER: 'laser_cutting',
};

const OnboardingWizard = ({ onComplete, onSkip }) => {
  const { user: contextUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const token = localStorage.getItem('token');

  // Fetch the freshest user data directly from the API on mount
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => {
        setCurrentUser(res.data);
      })
      .catch(err => {
        console.error('[OnboardingWizard] Failed to fetch user:', err);
        setCurrentUser(contextUser);
      })
      .finally(() => setUserLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showWhyInfo, setShowWhyInfo] = useState({});
  
  // Form data
  const [formData, setFormData] = useState({
    // Step 1 — module locked to session's active_module chosen at login
    selected_module: getActiveModule(),
    units: 'metric',
    experience_level: 'beginner',
    // Step 2 — CNC
    machine_class: 'hobby',
    spindle_type: 'trim_router',
    max_rpm: 24000,
    typical_material: '',
    // Step 2 — 3D Printing
    printer_type: 'fdm',
    build_volume_x: '',
    build_volume_y: '',
    build_volume_z: '',
    nozzle_diameter: '0.4',
    heated_bed: true,
    // Step 2 — Laser Cutting
    laser_type: 'co2',
    wattage: '',
    bed_size_x: '',
    bed_size_y: '',
    air_assist: false,
    // Step 3 (CNC only)
    tool_library_option: 'recommended',
    // Step 4 (CNC only)
    cam_export_target: 'aspire',
    safety_style: 'conservative'
  });

  const updateFormData = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
  };

  const toggleWhyInfo = (key) => {
    setShowWhyInfo(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // CNC has 4 steps; other modules have 3 (Module+Prefs → Machine → Ready)
  const totalSteps = formData.selected_module === MODULE_KEYS.CNC ? TOTAL_STEPS : 3;

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = async () => {
    setIsSubmitting(true);
    const headers = { Authorization: `Bearer ${token}` };
    try {
      switch (formData.selected_module) {
        case MODULE_KEYS.CNC:
          await axios.post(`${API}/onboarding`, { ...formData, completed: true }, { headers });
          if (formData.tool_library_option === 'recommended') {
            await axios.post(`${API}/onboarding/create-starter-tools`, {}, { headers });
          }
          toast.success("Setup complete! You're ready to start cutting.");
          break;

        case MODULE_KEYS.PRINT:
          await axios.post(`${API}/onboarding`, {
            units: formData.units,
            experience_level: formData.experience_level,
            completed: true,
          }, { headers });
          await axios.post(`${API}/printing3d/printers`, {
            name: `My ${formData.printer_type.toUpperCase()} Printer`,
            printer_type: formData.printer_type,
            build_volume_x: parseFloat(formData.build_volume_x) || 0,
            build_volume_y: parseFloat(formData.build_volume_y) || 0,
            build_volume_z: parseFloat(formData.build_volume_z) || 0,
            nozzle_diameter: parseFloat(formData.nozzle_diameter) || 0.4,
            heated_bed: formData.heated_bed,
            units: formData.units,
          }, { headers });
          toast.success("Setup complete! You're ready to start printing.");
          break;

        case MODULE_KEYS.LASER:
          await axios.post(`${API}/onboarding`, {
            units: formData.units,
            experience_level: formData.experience_level,
            completed: true,
          }, { headers });
          await axios.post(`${API}/laser/machines`, {
            name: `My ${formData.laser_type.toUpperCase()} Laser`,
            laser_type: formData.laser_type,
            wattage: parseFloat(formData.wattage) || 0,
            bed_size_x: parseFloat(formData.bed_size_x) || 0,
            bed_size_y: parseFloat(formData.bed_size_y) || 0,
            air_assist: formData.air_assist,
            units: formData.units,
          }, { headers });
          toast.success("Setup complete! You're ready to start cutting.");
          break;

        default:
          toast.error('Cannot complete setup: no module selected.');
          return;
      }

      onComplete?.();
    } catch (error) {
      console.error('Failed to save onboarding:', error);
      toast.error('Failed to save settings. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    if (window.confirm('Are you sure? Without setup, calculations will use generic defaults that may not match your machine.')) {
      onSkip?.();
    }
  };

  // Step 1: Module (read-only session lock) + Units + Experience
  const renderStep1 = () => {
    const activeMeta = MODULE_META[formData.selected_module];
    const ActiveIcon = activeMeta?.Icon || Router;
    return (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <Ruler className="w-12 h-12 mx-auto text-[#e67e22] mb-3" />
        <h2 className="text-2xl font-bold text-white">Your Session Module</h2>
        <p className="text-[#a3a3a3] mt-2">You're set up for this module. Set your preferences below.</p>
      </div>

      {/* Read-only session-locked module card */}
      <div className="space-y-3">
        <Label className="text-white font-medium">Active Module</Label>
        <div className="flex items-center gap-3 p-4 rounded-lg border border-[#e67e22] bg-[#e67e22]/10">
          <ActiveIcon className="w-5 h-5 flex-shrink-0 text-[#e67e22]" />
          <div className="flex-1">
            <div className="font-medium text-white">{activeMeta?.label}</div>
            <div className="text-xs text-[#a3a3a3]">{activeMeta?.desc}</div>
          </div>
          <Lock className="w-4 h-4 text-zinc-500" />
        </div>
        <p className="text-xs text-zinc-500">
          To switch modules, log out and select a different edition at login.
        </p>
      </div>

      {/* Units */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-white font-medium">Measurement Units</Label>
          <Collapsible open={showWhyInfo.units} onOpenChange={() => toggleWhyInfo('units')}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="text-[#a3a3a3] hover:text-white h-6 px-2">
                <HelpCircle className="w-4 h-4 mr-1" />
                Why?
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 p-3 bg-[#2a2a2a] rounded-lg text-sm text-[#a3a3a3]">
              Your unit choice affects all calculations, tool exports, and display values. 
              Most CAM software can work with either, so choose what you're comfortable with.
            </CollapsibleContent>
          </Collapsible>
        </div>
        <RadioGroup 
          value={formData.units} 
          onValueChange={(v) => updateFormData('units', v)}
          className="grid grid-cols-2 gap-3"
        >
          <Label 
            htmlFor="metric" 
            className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              formData.units === 'metric' 
                ? 'border-[#e67e22] bg-[#e67e22]/10' 
                : 'border-[#3a3a3a] hover:border-[#4a4a4a]'
            }`}
          >
            <RadioGroupItem value="metric" id="metric" />
            <div>
              <div className="font-medium text-white">Metric (mm)</div>
              <div className="text-xs text-[#a3a3a3]">Millimeters</div>
            </div>
          </Label>
          <Label 
            htmlFor="imperial" 
            className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              formData.units === 'imperial' 
                ? 'border-[#e67e22] bg-[#e67e22]/10' 
                : 'border-[#3a3a3a] hover:border-[#4a4a4a]'
            }`}
          >
            <RadioGroupItem value="imperial" id="imperial" />
            <div>
              <div className="font-medium text-white">Imperial (in)</div>
              <div className="text-xs text-[#a3a3a3]">Inches</div>
            </div>
          </Label>
        </RadioGroup>
      </div>

      {/* Experience Level */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-white font-medium">Experience Level</Label>
          <Collapsible open={showWhyInfo.experience} onOpenChange={() => toggleWhyInfo('experience')}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="text-[#a3a3a3] hover:text-white h-6 px-2">
                <HelpCircle className="w-4 h-4 mr-1" />
                Why?
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 p-3 bg-[#2a2a2a] rounded-lg text-sm text-[#a3a3a3]">
              Beginners get more conservative defaults and extra warnings. 
              Advanced users see optional parameters but still get safe defaults.
            </CollapsibleContent>
          </Collapsible>
        </div>
        <RadioGroup 
          value={formData.experience_level} 
          onValueChange={(v) => updateFormData('experience_level', v)}
          className="space-y-2"
        >
          <Label 
            htmlFor="beginner" 
            className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              formData.experience_level === 'beginner' 
                ? 'border-[#e67e22] bg-[#e67e22]/10' 
                : 'border-[#3a3a3a] hover:border-[#4a4a4a]'
            }`}
          >
            <RadioGroupItem value="beginner" id="beginner" />
            <div className="flex-1">
              <div className="font-medium text-white">Beginner</div>
              <div className="text-xs text-[#a3a3a3]">New to CNC or prefer safer defaults with extra guidance</div>
            </div>
          </Label>
          <Label 
            htmlFor="intermediate" 
            className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              formData.experience_level === 'intermediate' 
                ? 'border-[#e67e22] bg-[#e67e22]/10' 
                : 'border-[#3a3a3a] hover:border-[#4a4a4a]'
            }`}
          >
            <RadioGroupItem value="intermediate" id="intermediate" />
            <div className="flex-1">
              <div className="font-medium text-white">Intermediate</div>
              <div className="text-xs text-[#a3a3a3]">Comfortable with basics, ready for balanced defaults</div>
            </div>
          </Label>
          <Label 
            htmlFor="advanced" 
            className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              formData.experience_level === 'advanced' 
                ? 'border-[#e67e22] bg-[#e67e22]/10' 
                : 'border-[#3a3a3a] hover:border-[#4a4a4a]'
            }`}
          >
            <RadioGroupItem value="advanced" id="advanced" />
            <div className="flex-1">
              <div className="font-medium text-white">Advanced</div>
              <div className="text-xs text-[#a3a3a3]">Show all parameters, but still use safe starting points</div>
            </div>
          </Label>
        </RadioGroup>
      </div>
    </div>
  );
  };

  // Module display metadata (used in Step 1 read-only card and Step 2 heading)
  const MODULE_META = {
    [MODULE_KEYS.CNC]:   { Icon: Router,  label: 'CNC Router',    desc: 'Feeds, speeds, tool management & G-code' },
    [MODULE_KEYS.PRINT]: { Icon: Printer, label: '3D Printing',   desc: 'Print calculator, troubleshooting & slicer guide' },
    [MODULE_KEYS.LASER]: { Icon: Flame,   label: 'Laser Cutting', desc: 'Power/speed settings, materials & safety' },
  };

  // Step 2: Machine setup — dynamic heading + fields based on selected module
  const STEP2_META = {
    [MODULE_KEYS.CNC]:   { title: 'CNC Machine Setup',  subtitle: 'Tell us about your CNC router',   Icon: Router },
    [MODULE_KEYS.PRINT]: { title: '3D Printer Setup',   subtitle: 'Tell us about your 3D printer',   Icon: Printer },
    [MODULE_KEYS.LASER]: { title: 'Laser Cutter Setup', subtitle: 'Tell us about your laser system', Icon: Flame },
  };

  const renderCNCFields = () => (
    <>
      {/* Machine Class */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-white font-medium">Machine Class</Label>
          <Collapsible open={showWhyInfo.machine} onOpenChange={() => toggleWhyInfo('machine')}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="text-[#a3a3a3] hover:text-white h-6 px-2">
                <HelpCircle className="w-4 h-4 mr-1" />Why?
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 p-3 bg-[#2a2a2a] rounded-lg text-sm text-[#a3a3a3]">
              Machine rigidity affects safe cutting depths. Belt-driven hobby machines need shallower passes
              than rigid ballscrew machines to avoid chatter.
            </CollapsibleContent>
          </Collapsible>
        </div>
        <RadioGroup value={formData.machine_class} onValueChange={(v) => updateFormData('machine_class', v)} className="space-y-2">
          {[
            { value: 'hobby', label: 'Hobby / Light',       desc: 'Belt-driven, 3018-style, X-Carve, Shapeoko (older)' },
            { value: 'mid',   label: 'Mid-Range',           desc: 'Leadscrew, Shapeoko 4/5, OneFinity, Avid Benchtop' },
            { value: 'heavy', label: 'Heavy / Industrial',  desc: 'Ballscrew, Avid PRO, industrial VMC' },
          ].map(({ value, label, desc }) => (
            <Label key={value} htmlFor={`mc_${value}`}
              className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${formData.machine_class === value ? 'border-[#e67e22] bg-[#e67e22]/10' : 'border-[#3a3a3a] hover:border-[#4a4a4a]'}`}>
              <RadioGroupItem value={value} id={`mc_${value}`} />
              <div className="flex-1">
                <div className="font-medium text-white">{label}</div>
                <div className="text-xs text-[#a3a3a3]">{desc}</div>
              </div>
            </Label>
          ))}
        </RadioGroup>
      </div>

      {/* Spindle Type */}
      <div className="space-y-3">
        <Label className="text-white font-medium">Spindle Type</Label>
        <RadioGroup value={formData.spindle_type} onValueChange={(v) => updateFormData('spindle_type', v)} className="grid grid-cols-2 gap-3">
          {[
            { value: 'trim_router', label: 'Trim Router',  desc: 'Makita, DeWalt, Bosch' },
            { value: 'vfd_spindle', label: 'VFD Spindle',  desc: 'Water/air cooled spindle' },
          ].map(({ value, label, desc }) => (
            <Label key={value} htmlFor={`sp_${value}`}
              className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${formData.spindle_type === value ? 'border-[#e67e22] bg-[#e67e22]/10' : 'border-[#3a3a3a] hover:border-[#4a4a4a]'}`}>
              <RadioGroupItem value={value} id={`sp_${value}`} />
              <div>
                <div className="font-medium text-white">{label}</div>
                <div className="text-xs text-[#a3a3a3]">{desc}</div>
              </div>
            </Label>
          ))}
        </RadioGroup>
      </div>

      {/* Max RPM */}
      <div className="space-y-3">
        <Label className="text-white font-medium">Max Spindle RPM</Label>
        <Select value={String(formData.max_rpm)} onValueChange={(v) => updateFormData('max_rpm', parseInt(v))}>
          <SelectTrigger className="bg-[#2a2a2a] border-[#3a3a3a] text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#2a2a2a] border-[#3a3a3a]">
            <SelectItem value="18000">18,000 RPM</SelectItem>
            <SelectItem value="24000">24,000 RPM</SelectItem>
            <SelectItem value="30000">30,000 RPM (Trim Router)</SelectItem>
            <SelectItem value="32000">32,000 RPM</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Typical Material */}
      <div className="space-y-3">
        <Label className="text-white font-medium">What will you cut most often? (Optional)</Label>
        <Select value={formData.typical_material} onValueChange={(v) => updateFormData('typical_material', v)}>
          <SelectTrigger className="bg-[#2a2a2a] border-[#3a3a3a] text-white"><SelectValue placeholder="Select primary material..." /></SelectTrigger>
          <SelectContent className="bg-[#2a2a2a] border-[#3a3a3a]">
            <SelectItem value="softwood">Softwood (Pine, Cedar)</SelectItem>
            <SelectItem value="hardwood">Hardwood (Oak, Maple, Walnut)</SelectItem>
            <SelectItem value="mdf_plywood">MDF / Plywood</SelectItem>
            <SelectItem value="plastics">Plastics (Acrylic, HDPE)</SelectItem>
            <SelectItem value="aluminium">Aluminium</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </>
  );

  const render3DPFields = () => (
    <>
      {/* Printer Type */}
      <div className="space-y-3">
        <Label className="text-white font-medium">Printer Type</Label>
        <RadioGroup value={formData.printer_type} onValueChange={(v) => updateFormData('printer_type', v)} className="space-y-2">
          {[
            { value: 'fdm',   label: 'FDM',   desc: 'Filament-based — Ender, Bambu, Prusa, Voron' },
            { value: 'resin', label: 'Resin', desc: 'SLA/MSLA — Elegoo Mars, Anycubic, Phrozen' },
            { value: 'other', label: 'Other', desc: 'SLS, MJF, or other technology' },
          ].map(({ value, label, desc }) => (
            <Label key={value} htmlFor={`pt_${value}`}
              className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${formData.printer_type === value ? 'border-[#e67e22] bg-[#e67e22]/10' : 'border-[#3a3a3a] hover:border-[#4a4a4a]'}`}>
              <RadioGroupItem value={value} id={`pt_${value}`} />
              <div className="flex-1">
                <div className="font-medium text-white">{label}</div>
                <div className="text-xs text-[#a3a3a3]">{desc}</div>
              </div>
            </Label>
          ))}
        </RadioGroup>
      </div>

      {/* Build Volume */}
      <div className="space-y-3">
        <Label className="text-white font-medium">Build Volume (mm)</Label>
        <div className="grid grid-cols-3 gap-3">
          {['x', 'y', 'z'].map((axis) => (
            <div key={axis} className="space-y-1">
              <Label className="text-xs text-[#a3a3a3] uppercase">{axis}</Label>
              <input
                type="number"
                min="0"
                placeholder={axis === 'z' ? '250' : '220'}
                value={formData[`build_volume_${axis}`]}
                onChange={(e) => updateFormData(`build_volume_${axis}`, e.target.value)}
                className="w-full bg-[#2a2a2a] border border-[#3a3a3a] text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#e67e22]"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Nozzle Diameter */}
      <div className="space-y-3">
        <Label className="text-white font-medium">Nozzle Diameter</Label>
        <Select value={formData.nozzle_diameter} onValueChange={(v) => updateFormData('nozzle_diameter', v)}>
          <SelectTrigger className="bg-[#2a2a2a] border-[#3a3a3a] text-white"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-[#2a2a2a] border-[#3a3a3a]">
            <SelectItem value="0.2">0.2 mm (detail)</SelectItem>
            <SelectItem value="0.4">0.4 mm (standard)</SelectItem>
            <SelectItem value="0.6">0.6 mm (speed)</SelectItem>
            <SelectItem value="0.8">0.8 mm (draft)</SelectItem>
            <SelectItem value="other">Other</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Heated Bed */}
      <div className="space-y-3">
        <Label className="text-white font-medium">Heated Bed</Label>
        <RadioGroup value={formData.heated_bed ? 'yes' : 'no'} onValueChange={(v) => updateFormData('heated_bed', v === 'yes')} className="grid grid-cols-2 gap-3">
          {[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }].map(({ value, label }) => (
            <Label key={value} htmlFor={`hb_${value}`}
              className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${(formData.heated_bed ? 'yes' : 'no') === value ? 'border-[#e67e22] bg-[#e67e22]/10' : 'border-[#3a3a3a] hover:border-[#4a4a4a]'}`}>
              <RadioGroupItem value={value} id={`hb_${value}`} />
              <div className="font-medium text-white">{label}</div>
            </Label>
          ))}
        </RadioGroup>
      </div>

      {/* Units */}
      <div className="space-y-3">
        <Label className="text-white font-medium">Units</Label>
        <RadioGroup value={formData.units} onValueChange={(v) => updateFormData('units', v)} className="grid grid-cols-2 gap-3">
          {[{ value: 'metric', label: 'Metric (mm)' }, { value: 'imperial', label: 'Imperial (in)' }].map(({ value, label }) => (
            <Label key={value} htmlFor={`u3d_${value}`}
              className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${formData.units === value ? 'border-[#e67e22] bg-[#e67e22]/10' : 'border-[#3a3a3a] hover:border-[#4a4a4a]'}`}>
              <RadioGroupItem value={value} id={`u3d_${value}`} />
              <div className="font-medium text-white">{label}</div>
            </Label>
          ))}
        </RadioGroup>
      </div>
    </>
  );

  const renderLaserFields = () => (
    <>
      {/* Laser Type */}
      <div className="space-y-3">
        <Label className="text-white font-medium">Laser Type</Label>
        <RadioGroup value={formData.laser_type} onValueChange={(v) => updateFormData('laser_type', v)} className="space-y-2">
          {[
            { value: 'co2',   label: 'CO2',   desc: 'Glass tube — Sculpfun, Thunder, OMTech, Ruida' },
            { value: 'diode', label: 'Diode', desc: 'Solid state — xTool, Sculpfun S, Atomstack, Ortur' },
            { value: 'fibre', label: 'Fibre', desc: 'Metal marking — MOPA, RAYCUS fibre sources' },
          ].map(({ value, label, desc }) => (
            <Label key={value} htmlFor={`lt_${value}`}
              className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${formData.laser_type === value ? 'border-[#e67e22] bg-[#e67e22]/10' : 'border-[#3a3a3a] hover:border-[#4a4a4a]'}`}>
              <RadioGroupItem value={value} id={`lt_${value}`} />
              <div className="flex-1">
                <div className="font-medium text-white">{label}</div>
                <div className="text-xs text-[#a3a3a3]">{desc}</div>
              </div>
            </Label>
          ))}
        </RadioGroup>
      </div>

      {/* Wattage */}
      <div className="space-y-3">
        <Label className="text-white font-medium">Laser Wattage (W)</Label>
        <input
          type="number"
          min="0"
          placeholder="e.g. 40, 80, 130"
          value={formData.wattage}
          onChange={(e) => updateFormData('wattage', e.target.value)}
          className="w-full bg-[#2a2a2a] border border-[#3a3a3a] text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#e67e22]"
        />
      </div>

      {/* Bed Size */}
      <div className="space-y-3">
        <Label className="text-white font-medium">Bed Size (mm)</Label>
        <div className="grid grid-cols-2 gap-3">
          {[{ key: 'bed_size_x', label: 'Width (X)', ph: '400' }, { key: 'bed_size_y', label: 'Depth (Y)', ph: '400' }].map(({ key, label, ph }) => (
            <div key={key} className="space-y-1">
              <Label className="text-xs text-[#a3a3a3]">{label}</Label>
              <input
                type="number"
                min="0"
                placeholder={ph}
                value={formData[key]}
                onChange={(e) => updateFormData(key, e.target.value)}
                className="w-full bg-[#2a2a2a] border border-[#3a3a3a] text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:border-[#e67e22]"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Air Assist */}
      <div className="space-y-3">
        <Label className="text-white font-medium">Air Assist</Label>
        <RadioGroup value={formData.air_assist ? 'yes' : 'no'} onValueChange={(v) => updateFormData('air_assist', v === 'yes')} className="grid grid-cols-2 gap-3">
          {[{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }].map(({ value, label }) => (
            <Label key={value} htmlFor={`aa_${value}`}
              className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${(formData.air_assist ? 'yes' : 'no') === value ? 'border-[#e67e22] bg-[#e67e22]/10' : 'border-[#3a3a3a] hover:border-[#4a4a4a]'}`}>
              <RadioGroupItem value={value} id={`aa_${value}`} />
              <div className="font-medium text-white">{label}</div>
            </Label>
          ))}
        </RadioGroup>
      </div>

      {/* Units */}
      <div className="space-y-3">
        <Label className="text-white font-medium">Units</Label>
        <RadioGroup value={formData.units} onValueChange={(v) => updateFormData('units', v)} className="grid grid-cols-2 gap-3">
          {[{ value: 'metric', label: 'Metric (mm)' }, { value: 'imperial', label: 'Imperial (in)' }].map(({ value, label }) => (
            <Label key={value} htmlFor={`ulaser_${value}`}
              className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${formData.units === value ? 'border-[#e67e22] bg-[#e67e22]/10' : 'border-[#3a3a3a] hover:border-[#4a4a4a]'}`}>
              <RadioGroupItem value={value} id={`ulaser_${value}`} />
              <div className="font-medium text-white">{label}</div>
            </Label>
          ))}
        </RadioGroup>
      </div>
    </>
  );

  const renderStep2 = () => {
    let fields;
    let branchName;
    switch (formData.selected_module) {
      case MODULE_KEYS.CNC:
        fields = renderCNCFields();
        branchName = 'CNC';
        break;
      case MODULE_KEYS.PRINT:
        fields = render3DPFields();
        branchName = '3D_PRINTING';
        break;
      case MODULE_KEYS.LASER:
        fields = renderLaserFields();
        branchName = 'LASER';
        break;
      default:
        branchName = 'NONE';
        fields = (
          <div className="p-4 rounded-lg bg-red-900/40 border border-red-500/40 text-sm text-red-200">
            Internal error: unknown module "{formData.selected_module}"
          </div>
        );
    }
    const meta = STEP2_META[formData.selected_module] || STEP2_META[MODULE_KEYS.CNC];
    const { title, subtitle, Icon } = meta;
    console.log('[OnboardingWizard] Rendering Step 2 for module:', formData.selected_module);
    console.log('[OnboardingWizard] renderStep2 module:', formData.selected_module, 'matched branch:', branchName);
    return (
      <div className="space-y-6">
        <div className="text-center mb-6">
          <Icon className="w-12 h-12 mx-auto text-[#e67e22] mb-3" />
          <h2 className="text-2xl font-bold text-white">{title}</h2>
          <p className="text-[#a3a3a3] mt-2">{subtitle}</p>
        </div>
        {fields}
      </div>
    );
  };

  // Step 3 for non-CNC modules: summary + ready to complete
  const renderNonCNCReady = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <Rocket className="w-12 h-12 mx-auto text-[#e67e22] mb-3" />
        <h2 className="text-2xl font-bold text-white">Almost Ready!</h2>
        <p className="text-[#a3a3a3] mt-2">Review your setup and complete</p>
      </div>

      <div className="bg-gradient-to-r from-[#e67e22]/20 to-[#e67e22]/5 rounded-lg p-4 border border-[#e67e22]/30">
        <div className="text-sm font-medium text-[#e67e22] mb-3">Your Setup Summary</div>
        <div className="grid grid-cols-2 gap-2 text-xs text-[#a3a3a3]">
          <div>Units: <span className="text-white">{formData.units === 'metric' ? 'Metric (mm)' : 'Imperial (in)'}</span></div>
          <div>Experience: <span className="text-white capitalize">{formData.experience_level}</span></div>
          {formData.selected_module === MODULE_KEYS.PRINT && (
            <>
              <div>Printer Type: <span className="text-white uppercase">{formData.printer_type}</span></div>
              <div>Nozzle: <span className="text-white">{formData.nozzle_diameter} mm</span></div>
              <div>Heated Bed: <span className="text-white">{formData.heated_bed ? 'Yes' : 'No'}</span></div>
              {formData.build_volume_x && <div>Build Vol: <span className="text-white">{formData.build_volume_x}×{formData.build_volume_y}×{formData.build_volume_z} mm</span></div>}
            </>
          )}
          {formData.selected_module === MODULE_KEYS.LASER && (
            <>
              <div>Laser Type: <span className="text-white uppercase">{formData.laser_type}</span></div>
              <div>Wattage: <span className="text-white">{formData.wattage ? `${formData.wattage} W` : '—'}</span></div>
              <div>Air Assist: <span className="text-white">{formData.air_assist ? 'Yes' : 'No'}</span></div>
              {formData.bed_size_x && <div>Bed Size: <span className="text-white">{formData.bed_size_x}×{formData.bed_size_y} mm</span></div>}
            </>
          )}
        </div>
      </div>

      <div className="flex items-start gap-3 p-4 rounded-lg bg-green-900/30 border border-green-500/30">
        <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
        <p className="text-green-200 text-sm">
          Click <strong>Complete Setup</strong> to save your machine profile and enter the dashboard.
          You can update these details anytime in My Equipment.
        </p>
      </div>
    </div>
  );

  // Step 3: Tool Library (CNC only)
  const renderStep3 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <Wrench className="w-12 h-12 mx-auto text-[#e67e22] mb-3" />
        <h2 className="text-2xl font-bold text-white">Your Tools</h2>
        <p className="text-[#a3a3a3] mt-2">Set up your tool library</p>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-white font-medium">How would you like to set up your tools?</Label>
          <Collapsible open={showWhyInfo.tools} onOpenChange={() => toggleWhyInfo('tools')}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="text-[#a3a3a3] hover:text-white h-6 px-2">
                <HelpCircle className="w-4 h-4 mr-1" />
                Why?
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 p-3 bg-[#2a2a2a] rounded-lg text-sm text-[#a3a3a3]">
              Your tool library makes the calculator smarter. It pre-fills tool dimensions and 
              lets you export settings directly to your CAM software.
            </CollapsibleContent>
          </Collapsible>
        </div>
        
        <RadioGroup 
          value={formData.tool_library_option} 
          onValueChange={(v) => updateFormData('tool_library_option', v)}
          className="space-y-3"
        >
          <Label 
            htmlFor="recommended" 
            className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              formData.tool_library_option === 'recommended' 
                ? 'border-[#e67e22] bg-[#e67e22]/10' 
                : 'border-[#3a3a3a] hover:border-[#4a4a4a]'
            }`}
          >
            <RadioGroupItem value="recommended" id="recommended" className="mt-1" />
            <div className="flex-1">
              <div className="font-medium text-white flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-green-400" />
                Use recommended starter tools
              </div>
              <div className="text-xs text-[#a3a3a3] mt-1">
                We'll add a curated set of common tools: 3mm & 6.35mm end mills, compression bit, V-bits.
                You can add more later.
              </div>
              <div className="text-xs text-green-400 mt-2 font-medium">Recommended for beginners</div>
            </div>
          </Label>
          
          <Label 
            htmlFor="manual" 
            className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              formData.tool_library_option === 'manual' 
                ? 'border-[#e67e22] bg-[#e67e22]/10' 
                : 'border-[#3a3a3a] hover:border-[#4a4a4a]'
            }`}
          >
            <RadioGroupItem value="manual" id="manual" className="mt-1" />
            <div className="flex-1">
              <div className="font-medium text-white flex items-center gap-2">
                <Database className="w-4 h-4 text-blue-400" />
                I'll add tools manually later
              </div>
              <div className="text-xs text-[#a3a3a3] mt-1">
                Skip for now. You can add tools anytime in "My Tools".
              </div>
            </div>
          </Label>
          
          <Label 
            htmlFor="import" 
            className={`flex items-start gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              formData.tool_library_option === 'import' 
                ? 'border-[#e67e22] bg-[#e67e22]/10' 
                : 'border-[#3a3a3a] hover:border-[#4a4a4a]'
            }`}
          >
            <RadioGroupItem value="import" id="import" className="mt-1" />
            <div className="flex-1">
              <div className="font-medium text-white flex items-center gap-2">
                <Upload className="w-4 h-4 text-purple-400" />
                Import from Aspire / VCarve
              </div>
              <div className="text-xs text-[#a3a3a3] mt-1">
                Already have tools in Vectric? Import your .tools file after setup.
              </div>
            </div>
          </Label>
        </RadioGroup>
      </div>

      {formData.tool_library_option === 'recommended' && (
        <div className="bg-[#1f1f1f] rounded-lg p-4 border border-[#3a3a3a]">
          <div className="text-sm font-medium text-white mb-3">Starter tools we'll add:</div>
          <div className="grid grid-cols-2 gap-2 text-xs text-[#a3a3a3]">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#e67e22] rounded-full" />
              3mm 2-Flute Upcut
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#e67e22] rounded-full" />
              6.35mm 2-Flute Upcut
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#e67e22] rounded-full" />
              6.35mm Compression
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#e67e22] rounded-full" />
              60° V-Bit
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-[#e67e22] rounded-full" />
              90° V-Bit
            </div>
          </div>
        </div>
      )}
    </div>
  );

  // Step 4: Workflow & Quick Test
  const renderStep4 = () => (
    <div className="space-y-6">
      <div className="text-center mb-6">
        <Rocket className="w-12 h-12 mx-auto text-[#e67e22] mb-3" />
        <h2 className="text-2xl font-bold text-white">Almost Ready!</h2>
        <p className="text-[#a3a3a3] mt-2">Final preferences and you're set</p>
      </div>

      {/* CAM Export Target */}
      <div className="space-y-3">
        <Label className="text-white font-medium">Which CAM software do you use?</Label>
        <RadioGroup 
          value={formData.cam_export_target} 
          onValueChange={(v) => updateFormData('cam_export_target', v)}
          className="space-y-2"
        >
          <Label 
            htmlFor="aspire" 
            className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              formData.cam_export_target === 'aspire' 
                ? 'border-[#e67e22] bg-[#e67e22]/10' 
                : 'border-[#3a3a3a] hover:border-[#4a4a4a]'
            }`}
          >
            <RadioGroupItem value="aspire" id="aspire" />
            <div className="flex-1">
              <div className="font-medium text-white">Vectric Aspire / VCarve</div>
              <div className="text-xs text-[#a3a3a3]">Exports .TOOL files</div>
            </div>
          </Label>
          <Label 
            htmlFor="fusion360" 
            className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              formData.cam_export_target === 'fusion360' 
                ? 'border-[#e67e22] bg-[#e67e22]/10' 
                : 'border-[#3a3a3a] hover:border-[#4a4a4a]'
            }`}
          >
            <RadioGroupItem value="fusion360" id="fusion360" />
            <div className="flex-1">
              <div className="font-medium text-white">Fusion 360</div>
              <div className="text-xs text-[#a3a3a3]">Exports JSON tool library</div>
            </div>
          </Label>
          <Label 
            htmlFor="carbide_create" 
            className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              formData.cam_export_target === 'carbide_create' 
                ? 'border-[#e67e22] bg-[#e67e22]/10' 
                : 'border-[#3a3a3a] hover:border-[#4a4a4a]'
            }`}
          >
            <RadioGroupItem value="carbide_create" id="carbide_create" />
            <div className="flex-1">
              <div className="font-medium text-white">Carbide Create</div>
              <div className="text-xs text-[#a3a3a3]">Exports CSV (beginner-friendly)</div>
            </div>
          </Label>
        </RadioGroup>
      </div>

      {/* Safety Style */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <Label className="text-white font-medium">Calculation Safety Style</Label>
          <Collapsible open={showWhyInfo.safety} onOpenChange={() => toggleWhyInfo('safety')}>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm" className="text-[#a3a3a3] hover:text-white h-6 px-2">
                <HelpCircle className="w-4 h-4 mr-1" />
                Why?
              </Button>
            </CollapsibleTrigger>
            <CollapsibleContent className="mt-2 p-3 bg-[#2a2a2a] rounded-lg text-sm text-[#a3a3a3]">
              Conservative = slower feeds, shallower cuts, more warnings. Great for learning.
              Balanced = standard recommendations. Fast = pushes closer to limits (advanced).
            </CollapsibleContent>
          </Collapsible>
        </div>
        <RadioGroup 
          value={formData.safety_style} 
          onValueChange={(v) => updateFormData('safety_style', v)}
          className="space-y-2"
        >
          <Label 
            htmlFor="conservative" 
            className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              formData.safety_style === 'conservative' 
                ? 'border-[#e67e22] bg-[#e67e22]/10' 
                : 'border-[#3a3a3a] hover:border-[#4a4a4a]'
            }`}
          >
            <RadioGroupItem value="conservative" id="conservative" />
            <div className="flex-1">
              <div className="font-medium text-white flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-green-400" />
                Conservative
              </div>
              <div className="text-xs text-[#a3a3a3]">Safer defaults, more warnings. Best for learning.</div>
            </div>
          </Label>
          <Label 
            htmlFor="balanced" 
            className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              formData.safety_style === 'balanced' 
                ? 'border-[#e67e22] bg-[#e67e22]/10' 
                : 'border-[#3a3a3a] hover:border-[#4a4a4a]'
            }`}
          >
            <RadioGroupItem value="balanced" id="balanced" />
            <div className="flex-1">
              <div className="font-medium text-white">Balanced</div>
              <div className="text-xs text-[#a3a3a3]">Standard recommendations for typical setups.</div>
            </div>
          </Label>
          <Label 
            htmlFor="fast" 
            className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
              formData.safety_style === 'fast' 
                ? 'border-[#e67e22] bg-[#e67e22]/10' 
                : 'border-[#3a3a3a] hover:border-[#4a4a4a]'
            }`}
          >
            <RadioGroupItem value="fast" id="fast" />
            <div className="flex-1">
              <div className="font-medium text-white">Fast</div>
              <div className="text-xs text-[#a3a3a3]">Pushes closer to limits. For experienced users.</div>
            </div>
          </Label>
        </RadioGroup>
      </div>

      {/* Summary Preview */}
      <div className="bg-gradient-to-r from-[#e67e22]/20 to-[#e67e22]/5 rounded-lg p-4 border border-[#e67e22]/30">
        <div className="text-sm font-medium text-[#e67e22] mb-3">Your Setup Summary</div>
        <div className="grid grid-cols-2 gap-2 text-xs text-[#a3a3a3]">
          <div>Units: <span className="text-white">{formData.units === 'metric' ? 'Metric (mm)' : 'Imperial (in)'}</span></div>
          <div>Experience: <span className="text-white capitalize">{formData.experience_level}</span></div>
          <div>Machine: <span className="text-white capitalize">{formData.machine_class}</span></div>
          <div>Spindle: <span className="text-white">{formData.spindle_type === 'trim_router' ? 'Trim Router' : 'VFD Spindle'}</span></div>
          <div>Max RPM: <span className="text-white">{formData.max_rpm.toLocaleString()}</span></div>
          <div>CAM: <span className="text-white capitalize">{formData.cam_export_target.replace('_', ' ')}</span></div>
        </div>
      </div>
    </div>
  );

  // Completion screen
  const renderComplete = () => (
    <div className="text-center space-y-6 py-8">
      <div className="w-20 h-20 mx-auto bg-green-500/20 rounded-full flex items-center justify-center">
        <CheckCircle className="w-12 h-12 text-green-400" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-white">You're Ready to Cut!</h2>
        <p className="text-[#a3a3a3] mt-2">Your settings have been saved. Here's what to do next:</p>
      </div>
      
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-8">
        <Button
          onClick={() => navigate('/calculator')}
          className="flex flex-col items-center gap-2 h-auto py-6 bg-[#e67e22] hover:bg-[#d35400] text-white"
        >
          <Calculator className="w-8 h-8" />
          <span className="font-medium">Go to Calculator</span>
          <span className="text-xs opacity-80">Start your first job</span>
        </Button>
        
        <Button
          onClick={() => navigate('/knowledge/tools')}
          variant="outline"
          className="flex flex-col items-center gap-2 h-auto py-6 border-[#3a3a3a] text-white hover:bg-[#2a2a2a]"
        >
          <Wrench className="w-8 h-8" />
          <span className="font-medium">View My Tools</span>
          <span className="text-xs opacity-80">Manage your library</span>
        </Button>
        
        <Button
          onClick={() => navigate('/troubleshooting')}
          variant="outline"
          className="flex flex-col items-center gap-2 h-auto py-6 border-[#3a3a3a] text-white hover:bg-[#2a2a2a]"
        >
          <BookOpen className="w-8 h-8" />
          <span className="font-medium">Troubleshooting</span>
          <span className="text-xs opacity-80">Learn the basics</span>
        </Button>
      </div>
    </div>
  );

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1: return renderStep1();
      case 2: return renderStep2();
      case 3: return formData.selected_module === MODULE_KEYS.CNC ? renderStep3() : renderNonCNCReady();
      case 4: return renderStep4(); // CNC only
      default: return null;
    }
  };

  if (userLoading) {
    return (
      <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6 flex items-center justify-center" style={bgStyle(IMAGES.MACHINE_SETUP_WIZARD)}>
        <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="text-center">
          <Settings2 className="w-12 h-12 text-[#e67e22] mx-auto mb-4 animate-spin" />
          <p className="text-zinc-400">Loading your profile...</p>
        </div>
      </div>
    );
  }

  // No active session module — redirect to login to select one
  if (!formData.selected_module) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <div className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6 flex items-center justify-center" style={bgStyle(IMAGES.MACHINE_SETUP_WIZARD)}>
      <div className="fixed inset-0 bg-black/90 -z-10" style={{ pointerEvents: 'none' }} />
      <Card className="w-full max-w-2xl bg-[#1f1f1f] border-[#3a3a3a]">
        <CardHeader className="border-b border-[#3a3a3a]">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <img
                  src={require('@/assets/logo.png')}
                  alt="CNC-Helpdesk"
                  className="h-10 md:h-12 w-auto drop-shadow-lg"
                  style={{ background: 'transparent' }}
                />
              </CardTitle>
              <CardDescription className="text-[#a3a3a3] mt-1">
                Let's get your workshop set up
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleSkip}
              className="text-[#a3a3a3] hover:text-white"
            >
              Skip for now
            </Button>
          </div>
          
          {/* Progress */}
          <div className="mt-4">
            <div className="flex items-center justify-between text-xs text-[#a3a3a3] mb-2">
              <span>Step {currentStep} of {totalSteps}</span>
              <span>{Math.round((currentStep / totalSteps) * 100)}% complete</span>
            </div>
            <Progress value={(currentStep / totalSteps) * 100} className="h-2 bg-[#2a2a2a]" />
          </div>
        </CardHeader>
        
        <CardContent className="p-6">
          {renderCurrentStep()}
        </CardContent>
        
        {/* Navigation */}
        <div className="px-6 pb-6 flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="border-[#3a3a3a] text-white hover:bg-[#2a2a2a] disabled:opacity-50"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          
          {currentStep < totalSteps ? (
            <Button
              onClick={handleNext}
              className="bg-[#e67e22] hover:bg-[#d35400] text-white"
            >
              Continue
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              onClick={handleComplete}
              disabled={isSubmitting}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isSubmitting ? 'Saving...' : 'Complete Setup'}
              <CheckCircle className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      </Card>
    </div>
  );
};

export default OnboardingWizard;
